import { useState, useEffect } from 'react';
import betClient from '../../api/betClient';
import TodaysSlate from './TodaysSlate';
import { renderInlineMarkdown } from '../../utils/renderMarkdown';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';
import { useAuth } from '../../contexts/AuthContext';
import LockedContentOverlay from '../upgrade/LockedContentOverlay';

function renderBlock(text, i) {
  // Strip markdown headers (### Header -> Header)
  const stripped = text.replace(/^#{1,4}\s+/, '');
  // Check if it's a bullet list
  const lines = stripped.split('\n');
  const isList = lines.every((l) => l.trim().startsWith('- ') || l.trim() === '');
  if (isList && lines.some((l) => l.trim().startsWith('- '))) {
    return (
      <ul key={i} className="text-[14px] leading-[1.7] text-[#1F2937] dark:text-[#8892a8] mb-4 list-disc pl-5" style={{ fontFamily: 'var(--font-sans)' }}>
        {lines.filter((l) => l.trim().startsWith('- ')).map((l, j) => (
          <li key={j} className="mb-1">{renderInlineMarkdown(l.trim().slice(2))}</li>
        ))}
      </ul>
    );
  }
  return (
    <p key={i} className="text-[14px] leading-[1.7] text-[#1F2937] dark:text-[#8892a8] mb-4" style={{ fontFamily: 'var(--font-sans)' }}>
      {renderInlineMarkdown(stripped)}
    </p>
  );
}

export default function DailyBriefing() {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tier = useEffectiveTier();
  const { loading: authLoading } = useAuth();

  function fetch(force = false, signal = null) {
    setLoading(true); setError(null);
    const config = signal ? { signal } : {};
    betClient.get(`/api/ai/daily-briefing${force ? '?force=true' : ''}`, config)
      .then((r) => setBriefing(r.data))
      .catch((e) => { if (!signal?.aborted) setError(e.message); })
      .finally(() => { if (!signal?.aborted) setLoading(false); });
  }

  // Gate on auth settling so only one fetch fires with the correct tier.
  // AbortController cancels any in-flight request when tier changes mid-flight.
  useEffect(() => {
    if (authLoading) return;
    const controller = new AbortController();
    fetch(false, controller.signal);
    return () => controller.abort();
  }, [tier, authLoading]);

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="px-5 py-6">
      <p className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6] mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Daily Briefing</p>
      <h1 className="text-[22px] font-semibold text-[#111827] dark:text-[#e2e8f0] tracking-tight mb-1" style={{ fontFamily: 'var(--font-sans)' }}>{today}</h1>
      <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mb-6" style={{ fontFamily: 'var(--font-sans)' }}>AI-powered morning analysis</p>

      <div className="max-w-[1100px]">
        {loading ? (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2">
              <svg className="animate-spin h-3.5 w-3.5 text-[#005BB3]" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              <span className="text-[#6B7280] text-[13px]" style={{ fontFamily: 'var(--font-sans)' }}>Generating...</span>
            </div>
            {[...Array(4)].map((_, i) => <div key={i} className="space-y-1.5"><div className="h-3 rounded skeleton w-full" /><div className="h-3 rounded skeleton w-11/12" /><div className="h-3 rounded skeleton w-4/5" /></div>)}
          </div>
        ) : error ? (
          <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2 text-center">
            <p className="text-[#DC2626] text-[13px]">Failed to load.</p>
            <button onClick={() => fetch(false)} className="text-[12px] text-[#DC2626] underline mt-1">Retry</button>
          </div>
        ) : briefing ? (
          <div>
            {!briefing.full ? (
              <>
                <div className="border-l-[3px] border-[#005BB3] pl-4 mb-5" style={{ fontFamily: 'var(--font-sans)' }}>
                  <p className="text-[16px] leading-[1.7] text-[#1F2937] dark:text-[#e2e8f0] font-medium">
                    {renderInlineMarkdown(briefing.content.replace(/^#{1,4}\s+/, '').trim())}
                  </p>
                </div>
                <div className="mb-4">
                  <LockedContentOverlay
                    description="Read the full morning briefing — covering all sports, sharp action, and value plays for today."
                    triggerFeature="daily_briefing"
                  />
                </div>
              </>
            ) : (
              <article>
                {(briefing.content || '').split('\n\n').map((block, i) => renderBlock(block, i))}
              </article>
            )}
            <div className="flex items-center justify-between flex-wrap gap-2 py-3 border-t border-[#E5E7EB] dark:border-[#1e2536] mb-8">
              <div className="flex items-center gap-2">
                {briefing.cached && <span className="text-[11px] uppercase tracking-[0.5px] font-semibold text-[#9CA3AF] px-1.5 py-0.5 rounded bg-[#F8F9FA] dark:bg-[#1a2030]">Cached</span>}
                <span className="text-[13px] text-[#6B7280]" style={{ fontFamily: 'var(--font-sans)' }}>{new Date(briefing.created_at).toLocaleString()}</span>
              </div>
              <button onClick={() => fetch(true)} className="text-[13px] text-[#005BB3] dark:text-[#3b82f6] hover:text-[#004494] font-medium transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>Refresh</button>
            </div>
          </div>
        ) : null}
      </div>

      <TodaysSlate />
    </div>
  );
}
